import { ReactNode, useEffect } from "react";
import { PostHogProvider, usePostHog } from "@posthog/react";
import type { PostHogConfig } from "posthog-js";

import { toPhone } from "common/utils/formatting";
import { Optional } from "common/utils/types";

const USER_ID_PREFIX = "user-";

const postHogOptions: Partial<PostHogConfig> = {
  autocapture: false,
  capture_pageview: false,
  disable_session_recording: true,
};

/** matches ClientBundle._get_initial_user_data */
export type PostHogAnalyticsUser = {
  id: number;
  email: Optional<string>;
  phone_number: Optional<number>;
  first_name: Optional<string>;
  last_name: Optional<string>;
};

type PostHogAnalyticsProviderProps = {
  children: ReactNode;
  isEnabled: boolean;
  user: PostHogAnalyticsUser | null;
};

const PostHogUserIdentity = ({ user }: { user: PostHogAnalyticsUser | null }) => {
  const posthog = usePostHog();

  useEffect(() => {
    if (user) {
      posthog.identify(`${USER_ID_PREFIX}${user.id}`, {
        email: user.email,
        phone_number: toPhone(user.phone_number),
        first_name: user.first_name,
        last_name: user.last_name,
      });
    } else if (posthog.get_distinct_id().startsWith(USER_ID_PREFIX)) {
      posthog.reset();
    }
  }, [posthog, user]);

  return null;
};

const PostHogAnalyticsProvider = ({ children, isEnabled, user }: PostHogAnalyticsProviderProps) => {
  if (!isEnabled || !window.POSTHOG_PUBLIC_API_KEY) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider apiKey={window.POSTHOG_PUBLIC_API_KEY} options={postHogOptions}>
      <PostHogUserIdentity user={user} />
      {children}
    </PostHogProvider>
  );
};

export default PostHogAnalyticsProvider;
